import { useLazyQuery, useMutation, useQuery } from '@apollo/client';
import gql from 'graphql-tag';
import { useState } from 'react';
import { CardIdentity } from '~reducers';
import { useSelector } from '~Hooks';
import useModal from '~ComponentsImproved/Modal/useModal';
import { useLogger } from '../useLogger';
import { useAdyen } from '../useAdyen';
import {
  LoyaltyUserFields,
  LoyaltyCardLookup,
  LoyaltyCardLookupVariables,
  LoyaltyUsersSearch,
  LoyaltyUsersSearchVariables,
  GetUser,
  GetUserVariables,
  GetUser_User,
  CreateUser,
  CreateUserVariables,
  UpdateLoyaltyUser,
  UpdateLoyaltyUserVariables,
  GetLoyaltyGeneral,
} from './api/__queries__';
import {
  CARD_LOOKUP,
  CREATE_USER,
  GET_USER,
  LOYALTY_GENERAL_FIELDS,
  SEARCH_MEMBERS,
  UPDATE_MEMBER,
} from './api';

/**
 * A single loyalty user, individually fetched by id, always goes through
 * GetUser - it is the only query allowed to address one user directly. Its
 * `loyalty*` fields are remapped onto [[LoyaltyMember]] so every other
 * loyalty view can keep treating members the same way regardless of which
 * query produced them.
 */
const mapUserToMember = (user: GetUser_User): LoyaltyMember => ({
  __typename: 'LoyaltyUser',
  id: user.id,
  name: user.loyaltyName,
  email: user.loyaltyEmail,
  registeredAt: user.loyaltyRegisteredAt,
  points: user.loyaltyPoints,
  connectedCards: user.loyaltyConnectedCards,
  stampCards: user.loyaltyStampCards,
  currentMembershipLevel: user.loyaltyCurrentMembershipLevel,
  nextMembershipLevel: user.loyaltyNextMembershipLevel,
});

/** One loyalty member, exactly as the fragment above selects them. */
export type LoyaltyMember = LoyaltyUserFields;

export interface LoyaltySearchState {
  loading: boolean;
  /** Null until the first search completes, then always the latest results. */
  results: LoyaltyMember[] | null;
  error: string | null;
}

export interface CreateLoyaltyMemberInput {
  name: string;
  email: string;
  /** Raw digits; hashed server-side. */
  personalIdentityNumber?: string;
}

/**
 * The loyalty service, wrapped so components never touch the GraphQL layer.
 *
 * Everything runs no-cache: a member's stamps and level move with every order
 * they place, so nothing here is safe to reuse.
 */
export const useLoyalty = () => {
  const logger = useLogger('loyalty-api');
  const adyen = useAdyen();
  const loyaltyModal = useModal();

  const [runCardLookup] = useLazyQuery<LoyaltyCardLookup, LoyaltyCardLookupVariables>(
    CARD_LOOKUP,
    { fetchPolicy: 'no-cache' },
  );
  const [runSearch, searchQuery] = useLazyQuery<LoyaltyUsersSearch, LoyaltyUsersSearchVariables>(
    SEARCH_MEMBERS,
    { fetchPolicy: 'no-cache' },
  );
  const [runGetUser] = useLazyQuery<GetUser, GetUserVariables>(GET_USER, {
    fetchPolicy: 'no-cache',
  });
  const [runCreate] = useMutation<CreateUser, CreateUserVariables>(CREATE_USER);
  const [runUpdate] = useMutation<UpdateLoyaltyUser, UpdateLoyaltyUserVariables>(UPDATE_MEMBER);
  const loyaltyGeneralQuery = useQuery<GetLoyaltyGeneral>(LOYALTY_GENERAL_FIELDS);

  const companyId = useSelector((state) => state.company);
  const defaultLanguage = useSelector((state) => state.defaultLanguage);

  /**
   * Who owns a tapped card. Null when the card matched nobody; throws when the
   * lookup itself failed.
   *
   * The PAR is the same value the backend stores as the card token when a
   * connected card pays, so this is an exact match, not a fuzzy one. Cards
   * that come back without a PAR fall back to the masked PAN, which will
   * normally match nothing - the caller's not-found flow covers it.
   */
  const identifyByCard = async (card: CardIdentity): Promise<LoyaltyMember | null> => {
    const token = card.par || card.maskedPan;

    logger.info('Loyalty card lookup started', {
      hasPar: Boolean(card.par),
      entryMode: card.entryMode,
    });

    if (!token) {
      logger.warn('Card acquisition returned neither PAR nor masked PAN');
      return null;
    }

    const result = await runCardLookup({ variables: { search: token } });

    if (result.error) {
      logger.error('Loyalty card lookup failed', { reason: result.error.message });
      throw new Error(result.error.message);
    }

    const member = result.data?.operativeViewer.loyaltyUsers[0] ?? null;

    logger.info('Loyalty card lookup completed', { found: Boolean(member), memberId: member?.id });

    return member;
  };

  /** Refetches one member, for a view that has only their id. Throws on failure. */
  const getMember = async (id: string): Promise<LoyaltyMember> => {
    const result = await runGetUser({ variables: { id } });
    const user = result.data?.User;

    if (!user) {
      logger.error('Could not load the loyalty member', {
        memberId: id,
        reason: result.error?.message,
      });
      throw new Error(result.error?.message ?? 'Empty User response');
    }

    return mapUserToMember(user);
  };

  /** Fires a search; results land in [[searchState]]. */
  const search = (term: string, first = 10) => {
    // The term itself is a name, email or personnummer - log its shape only.
    logger.info('Loyalty member search', { termLength: term.length });
    runSearch({ variables: { search: term, first } });
  };

  const searchState: LoyaltySearchState = {
    loading: searchQuery.loading,
    results: searchQuery.data ? searchQuery.data.operativeViewer.loyaltyUsers : null,
    error: searchQuery.error?.message ?? null,
  };

  /** How many times to retry the follow-up update mutation before giving up. */
  const UPDATE_MEMBER_MAX_RETRIES = 4;

  /**
   * Enrols a member: creates the underlying user, then updates the resulting
   * loyalty member with the enrolment details. Both steps must succeed - the
   * update is retried up to [[UPDATE_MEMBER_MAX_RETRIES]] times, and if it
   * still hasn't succeeded, the whole enrolment throws.
   */
  const createMember = async (input: CreateLoyaltyMemberInput): Promise<LoyaltyMember> => {
    const createResult = await runCreate({
      variables: {
        data: {
          name: input.name,
          email: input.email,
          language: defaultLanguage || 'en',
          company: {
            connect: { id: companyId },
          },
          creditBank: {
            create: {
              amount: 0,
            },
          },
        },
      },
    });

    const createdUser = createResult.data?.createUser;

    if (!createdUser) {
      throw new Error(createResult.errors?.[0]?.message ?? 'Empty createUser response');
    }

    logger.info('Loyalty user created', { userId: createdUser.id });

    let lastError: string | undefined;

    for (let attempt = 1; attempt <= UPDATE_MEMBER_MAX_RETRIES; attempt += 1) {
      try {
        const updateResult = await runUpdate({
          variables: {
            updateLoyaltyUserId: createdUser.id,
            input: {
              name: input.name,
              email: input.email,
              personalIdentityNumber: input.personalIdentityNumber,
            },
          },
        });

        if (updateResult.data?.updateLoyaltyUser.success) {
          logger.info('Loyalty member updated', { memberId: createdUser.id, attempt });
          return getMember(createdUser.id);
        }

        lastError =
          updateResult.data?.updateLoyaltyUser.message ??
          updateResult.errors?.[0]?.message ??
          'Empty updateLoyaltyUser response';
      } catch (err) {
        lastError = err instanceof Error ? err.message : String(err);
      }

      logger.warn('Loyalty member update attempt failed', {
        memberId: createdUser.id,
        attempt,
        reason: lastError,
      });
    }

    throw new Error(lastError ?? 'Could not update the loyalty member');
  };

  /**
   * The card wait lives here rather than in LoyaltyModal so the modal can
   * close while it runs - staff keep ringing up the order, and callers can
   * show a pulse of their own wherever they surface [[isWaitingForCard]].
   */
  const [isWaitingForCard, setIsWaitingForCard] = useState(false);
  const [pendingCard, setPendingCard] = useState<CardIdentity | null>(null);
  const [pendingError, setPendingError] = useState<string | null>(null);

  const clearPending = () => {
    setPendingCard(null);
    setPendingError(null);
  };

  const waitForCard = async (terminalId?: string) => {
    if (!terminalId) {
      return;
    }

    loyaltyModal.hide();
    clearPending();
    setIsWaitingForCard(true);

    try {
      const res = await adyen.requestCard(terminalId);

      if (!res.success) {
        setPendingError(res.message ?? null);
        return;
      }

      // Whose card this is gets resolved by the modal once it reopens. The
      // acquisition is kept either way: the card is good for the payment
      // whether or not a member is behind it, and releasing it here would cost
      // the shopper a second tap.
      setPendingCard({
        par: res.par,
        maskedPan: res.maskedPan,
        brand: res.brand,
        entryMode: res.entryMode,
        reference: res.reference,
      });
    } finally {
      setIsWaitingForCard(false);
      // Reopen with whatever came back: a member, the miss, or the error.
      loyaltyModal.show();
    }
  };

  return {
    identifyByCard,
    getMember,
    search,
    searchState,
    createMember,
    loyaltyModal,
    isWaitingForCard,
    pendingCard,
    pendingError,
    waitForCard,
    clearPending,
    isLoyaltyFullySetup:
      loyaltyGeneralQuery.data?.operativeViewer.isLoyaltyFullySetup &&
      !loyaltyGeneralQuery.loading,
  };
};
